3
<script setup lang="ts">
// Components of Module
import { VueYandexMaps } from 'vue-yandex-maps'

// Constants
import { STATE_OF_MAP } from '@/constants/stateOfMap'

// Store
import { useDeliveryStore } from '@/store/delivery/delivery'
import { useMapSettingsStore } from '@/store/map-settings/map-settings'
import { useToggleStore } from '@/store/toggle/toggle'

// Types
import { IPointData, PointProperties } from '@/api/modules/delivery/types'
import { StateGeoAddress, TypesProvider } from '@/types/mapServices'
import { DeliveryAddress, PlacesSearch } from '@/store/delivery/delivery.types'
import { Success } from '@/api/results'

// Components
import AppProgress from '@/components/App/AppProgress.vue'
import YandexMapComponent from '@/components/Services/YandexMap/YandexMapComponent.vue'
import DeliveryMapError from '@/components/Delivery/DeliveryMapError.vue'
import GoogleMapComponent from '@/components/Services/GoogleMap/GoogleMapComponent.vue'

const emit = defineEmits(['setSelectPoint'])

const { $sentryCaptureException, $apiService } = useNuxtApp()

const route = useRoute()
const router = useRouter()

const { isMobileOrTablet } = useDevice()

const deliveryStore = useDeliveryStore()
const mapSettingsStore = useMapSettingsStore()
const { newFetchingApiEnabled } = useToggleStore()

const point = ref<null | PointProperties>(null)

const areas = computed(() => deliveryStore.areas)
const filteredPoints = computed(() => deliveryStore.filteredPoints)
const deliveryAddress = computed(() => deliveryStore.deliveryAddress)
const serviceSettings = mapSettingsStore.mapSettings.service_settings

const isStateLoadMap = computed(() => deliveryStore.stateLoadMap)

// Состояние что карта загружена
const isMapLoaded = computed(() => isStateLoadMap.value === STATE_OF_MAP.loaded)

// Инициализация модуля Яндекс карты в процессе setup
const { createYmapsOptions } = await import('vue-yandex-maps')

const initModuleYandexMap = () => {
  const { provider, api_key } = serviceSettings

  // Для гугла не требуется вручную прокидывать инстанс карты
  if (provider === TypesProvider.GOOGLE) return

  if (provider === TypesProvider.YANDEX) createYmapsOptions({ apikey: api_key })
}

onMounted(() => {
  initModuleYandexMap()

  deliveryStore.setStateLoadMap(STATE_OF_MAP.loaded)

  getSelectedPointOfSlug()
})

watch(VueYandexMaps.loadStatus, (newStatus: VueYandexMaps.LoadStatus) => {
  deliveryStore.setStateLoadMap(newStatus)
})

// Т.к модуль не предоставляет тип, property, оставляем any
watch(VueYandexMaps.loadError, (newLoadError: any) => {
  if (!newLoadError) return

  deliveryStore.setStateLoadMap(STATE_OF_MAP.error)

  $sentryCaptureException(VueYandexMaps.loadError)
})

watch(
  () => route.query.slug,
  () => getSelectedPointOfSlug(),
)

// Если изменился адрес от autocomplete и литерал адреса не пуст, то обновляем координаты
watch(
  deliveryAddress.value.address,
  (newAddress: DeliveryAddress) => {
    if (
      newAddress.place !== PlacesSearch.AUTOCOMPLETE ||
      !newAddress.address?.value
    )
      return

    handleNewAddress(newAddress.address.value)
  },
  { deep: true },
)

// Ищем координаты адреса от автокомплита
const handleNewAddress = async (newAddressOfSearch: string) => {
  const result = await $apiService.geocode.getCoordinatesByAddress(
    newAddressOfSearch,
  )

  if (!(result instanceof Success)) {
    showSnackbar({ message: result?.message })

    return
  }

  const coords: LatLng = [result.data.latitude, result.data.longitude]

  deliveryStore.setPointsDistance(coords)

  deliveryStore.setDeliveryCoordinates([coords[1], coords[0]])
}

const getLocationOfCoordinates = async (coords: number[]) => {
  deliveryStore.setStateAddress(StateGeoAddress.LOADING)

  // Получаем информацию о точке
  const reverseCoords: LatLng = [coords[1], coords[0]]

  const result = await $apiService.geocode.getAddressByCoordinates(
    reverseCoords,
  )

  const address = result instanceof Success && result ? result.data : null

  // Фиксируем место выбора и адрес
  deliveryStore.setDeliverySearch(PlacesSearch.MAP, address)
  deliveryStore.setStateAddress(StateGeoAddress.READY)

  // Рассчитываем дистанцию от клика маркера до маркеров точек
  deliveryStore.setPointsDistance(reverseCoords)
}

// Фиксирукем данные выбранной точки ресторана карты
const clickOnPointOnMap = (pointData: IPointData) => {
  emit('setSelectPoint', pointData.pointId)

  router.replace({ query: { slug: pointData.slug } })
}

const updateCenterMap = () => {
  if (deliveryStore.deliveryAddress.address.place === PlacesSearch.DEFAULT)
    return

  // Для повторного определения геолокации, сбрасываем поле поиска
  deliveryStore.setDeliverySearch(
    PlacesSearch.DEFAULT,
    deliveryStore.deliveryAddress.address.address,
  )
}

// Метод поиска ресторана по slug
const getSelectedPointOfSlug = () => {
  point.value = null

  const slug = route.query?.slug

  if (!slug) return

  point.value = deliveryStore.getPointBySlug(slug as string)

  emit('setSelectPoint', point?.value?.id)
}
</script>

<template>
  <div
    class="delivery-map"
    :class="{ 'delivery-map--mobile': isMobileOrTablet }"
  >
    <app-progress
      v-if="isStateLoadMap === STATE_OF_MAP.loading"
      :color="'$button-background'"
    />

    <yandex-map-component
      v-if="isMapLoaded && serviceSettings.provider === TypesProvider.YANDEX"
      class="delivery-map__yandex"
      :settings="serviceSettings"
      width="100%"
      :clicked-restaurant="point"
      :selected-address="deliveryAddress"
      :areas="areas"
      :points="filteredPoints"
      @click-to-map="getLocationOfCoordinates"
      @click-to-restaurant="clickOnPointOnMap"
      @update-center-map="updateCenterMap"
    />
    <google-map-component
      v-if="isMapLoaded && serviceSettings.provider === TypesProvider.GOOGLE"
      class="delivery-map__google"
      :settings="serviceSettings"
      :clicked-restaurant="point"
      :selected-address="deliveryAddress"
      :areas="areas"
      :points="filteredPoints"
      @click-to-map="getLocationOfCoordinates"
      @click-to-restaurant="clickOnPointOnMap"
      @update-center-map="updateCenterMap"
    />

    <delivery-map-error
      v-if="isStateLoadMap === STATE_OF_MAP.error"
      class="delivery-map__error"
    />

    <transition v-if="newFetchingApiEnabled" name="fade">
      <app-loader-rolls
        v-show="deliveryStore.isLoadingAreas"
        class="loader-areas"
        icon-size="21"
        absolute
      />
    </transition>
  </div>
</template>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.loader-areas {
  position: absolute;
  top: 15px;
  left: 50%;
  transform: translateX(-50%);
}

.delivery-map {
  position: relative;
  width: 100%;
  max-height: 750px;

  &__yandex {
    height: 100% !important;
  }

  &__google {
    width: 100%;
    height: 100%;
  }

  &__error {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
  }

  &--mobile {
    padding: 0 20px;
  }
}
</style>
